"use client";

import CountrySelectorGenerator from "@/components/NumberGenerator/CountrySelectorGenerator";
import ServiceSelectorGenerator from "@/components/NumberGenerator/ServiceSelectorGenerator";
import React, { useState, useEffect } from "react";
import { Hyphenated } from "@/utils/Hyphenated";
import { SERVICES as services } from "@/data/services-data";

export default function FreeTempGeneratorClient({ countries }) {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Results render in-page from ?country=&service=, so keep the selectors in
  // sync with the URL on mount and on back/forward.
  useEffect(() => {
    const syncFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const countryParam = params.get("country");
      const serviceParam = params.get("service");

      const countryMatch = countryParam
        ? countries.find((c) => Hyphenated(c.country_name) === countryParam)
        : null;
      setSelectedCountry(countryMatch || null);

      const serviceMatch = serviceParam
        ? services.find((s) => s.value === serviceParam)
        : null;
      setSelectedService(serviceMatch || null);

      setIsLoading(false);
    };

    syncFromUrl();
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [countries]);

  const handleCountryClick = (country) => {
    setSelectedCountry(country);
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
  };

  const handleSearch = () => {
    if (!selectedCountry || !selectedService || isLoading) {
      return;
    }

    setIsLoading(true);

    const countrySlug = Hyphenated(selectedCountry.country_name);
    const serviceValue = selectedService.value;

    // Update the URL in place — the results are rendered by
    // GeneratorDynamicContent on this same page, so no navigation.
    window.history.pushState(
      {},
      "",
      `/temporary-phone-number-generator?country=${countrySlug}&service=${serviceValue}`
    );

    window.dispatchEvent(
      new CustomEvent("generatorchange", {
        detail: { country: countrySlug, service: serviceValue },
      })
    );

    setIsLoading(false);

    setTimeout(() => {
      document
        .getElementById("generator-content")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <>
      <div className="bg-gradient-to-tr to-[#090EDB] from-[#E73DC4] rounded-2xl py-12 md:py-16 lg:py-20 px-4 md:px-8">
        <div className="max-w-2xl mx-auto w-full">
          {/* Header Section */}
          <div className="text-center mb-8 md:mb-10 space-y-3">
            <h1 className="font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              Free Temporary SMS Number Generator
            </h1>
            <h2 className="font-bold text-xl md:text-2xl lg:text-3xl text-white">
              Temporary Numbers for WhatsApp, Telegram, and More
            </h2>
            <p className="text-gray-100 text-sm md:text-base font-medium max-w-lg mx-auto">
            Want WhatsApp verification without a phone number? What about for Telegram or other online services? Our free temporary numbers let you set up new accounts safely and securely. Choose a country and service to generate a free temporary number for SMS verification without a phone number.
            </p>
          </div>

          {/* Form Section */}
          <div className="space-y-4">
            {/* Country Selector */}
            <div className="w-full max-w-md mx-auto">
              <label className="block text-white/90 text-sm font-semibold mb-2">
                Select Country
              </label>
              <CountrySelectorGenerator
                data={countries}
                value={selectedCountry}
                handleMenuItemClick={handleCountryClick}
              />
            </div>

            {/* Service Selector */}
            <div className="w-full max-w-md mx-auto">
              <label className="block text-white/90 text-sm font-semibold mb-2">
                Select Service
              </label>
              <ServiceSelectorGenerator
                value={selectedService?.label}
                handleMenuItemClick={handleServiceClick}
              />
            </div>

            {/* Search Button */}
            <div className="w-full max-w-md mx-auto pt-2">
              <button
                onClick={handleSearch}
                disabled={!selectedCountry || !selectedService || isLoading}
                className="w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:from-[#090EDB]/90 hover:to-[#E73DC4]/90 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold text-lg py-4 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                )}
                {isLoading ? "Searching..." : "Search Numbers"}
              </button>
            </div>
          </div>

          {/* Status Message */}
          {selectedCountry && selectedService && (
            <div className="mt-6 text-center">
              <p className="text-white/90 text-sm md:text-base font-medium">
                Searching for{" "}
                <span className="font-bold">
                  {selectedCountry.country_flag} {selectedCountry.country_name}
                </span>{" "}
                phone numbers for{" "}
                <span className="font-bold">{selectedService.label}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
